import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { ApiService } from '@core/api/api.service';
import { SiteVisitEvent, SiteVisitToday } from '@core/models/models';

const KIND_LABEL: Record<string, string> = {
  enter: 'כניסה',
  page: 'עמוד',
  action: 'פעולה',
  search: 'חיפוש'
};

@Component({
  selector: 'app-visit-log-admin',
  standalone: true,
  imports: [CommonModule],
  template: `
    <section class="page" dir="rtl">
      <header class="hero">
        <p class="eyebrow">תפעול</p>
        <div class="hero-row">
          <div>
            <h1>יומן ביקורים</h1>
            <p class="lede">
              כניסה, מעבר בין עמודים, ולחיצה על כפתור או קישור. ביטוי החיפוש נשמר, וגם כתובת ה-IP והמדינה. טקסט אחר שמקלידים לא נשמר.
            </p>
          </div>
          <button type="button" class="refresh" [disabled]="refreshing" (click)="refresh(true)">
            {{ refreshing ? 'מרענן…' : 'רענון' }}
          </button>
        </div>
      </header>

      @if (errorMessage) {
        <p class="banner" role="alert">{{ errorMessage }}</p>
      }

      @if (loading) {
        <p class="status">טוען את היומן…</p>
      } @else if (events.length === 0) {
        <p class="status">עדיין אין ביקורים.</p>
      } @else {
        <p class="count">
          היום {{ today.distinctVisitors }} מבקרים שונים, {{ today.distinctAddresses }} כתובות IP, {{ today.distinctAccounts }} חשבונות
        </p>
        <p class="count">{{ events.length }} אירועים אחרונים</p>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>מתי</th>
                <th>מי</th>
                <th>מיקום</th>
                <th>מה</th>
                <th>עמוד</th>
                <th>פעולה</th>
              </tr>
            </thead>
            <tbody>
              @for (event of events; track event.id) {
                <tr>
                  <td class="when">{{ formatWhen(event.occurredAt) }}</td>
                  <td>
                    <span class="who">{{ who(event) }}</span>
                    @if (event.displayName && event.email) {
                      <span class="email">{{ event.email }}</span>
                    }
                  </td>
                  <td>
                    <span class="where" dir="ltr">{{ event.clientIp || '—' }}</span>
                    @if (event.country) {
                      <span class="email">{{ event.country }}</span>
                    }
                  </td>
                  <td><span class="kind" [attr.data-kind]="event.kind">{{ kindLabel(event.kind) }}</span></td>
                  <td class="route" dir="auto">{{ event.route }}</td>
                  <td class="label" dir="auto">{{ event.label || '—' }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </section>
  `,
  styles: [`
    :host {
      display: block;
      color: #182019;
    }

    .page {
      padding: 1.25rem 1.25rem 3rem;
    }

    .hero {
      padding: 1.1rem 1.2rem;
      margin-bottom: 0.9rem;
      border: 1px solid #dce6dd;
      border-radius: 16px;
      background:
        radial-gradient(circle at 8% 10%, rgba(53, 138, 79, 0.14), transparent 34%),
        #f7fbf7;
    }

    .eyebrow {
      margin: 0 0 0.25rem;
      color: #29623b;
      font-size: 0.75rem;
      font-weight: 750;
      letter-spacing: 0.04em;
    }

    .hero-row {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      gap: 1rem;
    }

    h1 {
      margin: 0;
      font-size: 1.45rem;
      font-weight: 750;
    }

    .lede,
    .status,
    .count,
    .banner {
      margin: 0.35rem 0 0;
      color: #4d5c52;
      font-size: 0.92rem;
    }

    .banner {
      margin-bottom: 0.75rem;
      color: #8a3d32;
    }

    .refresh {
      flex: none;
      border: 1px solid #b7c9bb;
      background: #fff;
      color: #1d3b28;
      border-radius: 999px;
      padding: 0.45rem 0.9rem;
      font: inherit;
      font-weight: 650;
      cursor: pointer;
    }

    .refresh:disabled {
      opacity: 0.6;
      cursor: default;
    }

    .count {
      margin: 0 0 0.45rem;
      font-variant-numeric: tabular-nums;
    }

    .table-wrap {
      overflow-x: auto;
      border: 1px solid #dce6dd;
      border-radius: 12px;
      background: #fff;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.92rem;
    }

    th,
    td {
      padding: 0.55rem 0.75rem;
      text-align: right;
      vertical-align: top;
      border-bottom: 1px solid #e7eee8;
    }

    th {
      color: #5c6b62;
      font-size: 0.75rem;
      font-weight: 700;
      background: #f6faf6;
    }

    tr:last-child td {
      border-bottom: 0;
    }

    .when {
      white-space: nowrap;
      font-variant-numeric: tabular-nums;
      color: #3d4c43;
    }

    .who,
    .email,
    .where {
      display: block;
    }

    .where {
      font-variant-numeric: tabular-nums;
    }

    .email {
      color: #6b7a71;
      font-size: 0.8rem;
    }

    .route,
    .label {
      max-width: 22rem;
      overflow-wrap: anywhere;
    }

    .kind {
      display: inline-block;
      padding: 0.1rem 0.45rem;
      border-radius: 999px;
      background: #eef2ef;
      color: #3d4c43;
      font-size: 0.78rem;
      font-weight: 700;
    }

    .kind[data-kind='enter'] {
      background: #e5f4ea;
      color: #1f6b3a;
    }

    .kind[data-kind='action'] {
      background: #f8f0df;
      color: #7a5b16;
    }
  `]
})
export class VisitLogAdminComponent implements OnInit, OnDestroy {
  private readonly api = inject(ApiService);
  private timer: ReturnType<typeof setInterval> | null = null;
  private loadId = 0;

  events: SiteVisitEvent[] = [];
  today: SiteVisitToday = { distinctVisitors: 0, distinctAddresses: 0, distinctAccounts: 0 };
  loading = true;
  refreshing = false;
  errorMessage = '';

  ngOnInit(): void {
    void this.refresh(false);
    this.timer = setInterval(() => void this.refresh(false), 5000);
  }

  ngOnDestroy(): void {
    if (this.timer) clearInterval(this.timer);
  }

  async refresh(manual: boolean): Promise<void> {
    const id = ++this.loadId;
    if (manual) this.refreshing = true;
    try {
      const result = await firstValueFrom(this.api.listSiteVisits(300));
      if (id !== this.loadId) return;
      this.events = result.events;
      this.today = result.today;
      this.errorMessage = '';
    } catch {
      if (id !== this.loadId) return;
      this.errorMessage = 'לא הצלחנו לטעון את היומן.';
    } finally {
      if (id === this.loadId) {
        this.loading = false;
        this.refreshing = false;
      }
    }
  }

  kindLabel(kind: string): string {
    return KIND_LABEL[kind] ?? kind;
  }

  who(event: SiteVisitEvent): string {
    const name = event.displayName || event.email || `אורח ${event.visitorId.slice(0, 8)}`;
    return event.actor === 'agent' ? `סוכן · ${name}` : name;
  }

  formatWhen(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('he-IL', {
      timeZone: 'Asia/Jerusalem',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }
}
